import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxChange, MatCheckboxModule } from '@angular/material/checkbox';
import { MatOption } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { MatExpansionPanelActionRow } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { CountryCode } from 'libphonenumber-js';
import { BehaviorSubject } from 'rxjs';
import { UserContextProvider } from '../../../infrastructure/context/user-context-provider';
import { Logger } from '../../../infrastructure/logging/logger';
import { Country } from '../../../shared/address/Country';
import { State } from '../../../shared/address/State';
import { BackButtonComponent } from '../../../shared/components/back-button/back-button.component';
import { CountryService } from '../../../shared/reference/country.service';
import { ReferenceService } from '../../../shared/reference/reference.service';
import { formatPhoneNumber } from '../../../utilities/PhoneUtility';
import { Organization } from '../models/Organization';
import { OrganizationService } from '../services/organizations.service';
import { NotifyAdditionalGuestInfoDialogComponent } from './components/notify-additional-guest-info-dialog/notify-additional-guest-info-dialog.component';

@Component({
  selector: 'app-organization-form',
  templateUrl: './organization-form.component.html',
  styleUrls: ['./organization-form.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatProgressSpinner,
    MatIcon,
    MatFormFieldModule,
    ReactiveFormsModule,
    MatOption,
    MatCheckboxModule,
    MatSelectModule,
    MatInputModule,
    MatExpansionPanelActionRow,
    MatButtonModule,
    RouterModule,
    BackButtonComponent
  ]
})
export class OrganizationFormComponent implements OnInit {

  safelyServices: any[] = [
    { id: 0, name: 'Guest Verification and Insurance Protection' },
    { id: 1, name: 'Guest Verification Only' },
    { id: 2, name: 'Insurance Protection Only' }
  ]

  basicInformationForm!: FormGroup;
  addressForm!: FormGroup;

  organization!: Organization;
  states!: State[];
  countries!: Country[];

  loading = new BehaviorSubject<boolean>(false);
  organizationId?: string | null;
  existingOrganization: boolean = false;
  usCountrySelectedBoolean: boolean = true;

  constructor(
    private formBuilder: FormBuilder,
    private organizationService: OrganizationService,
    private referenceService: ReferenceService,
    private countryService: CountryService,
    private userContextProvider: UserContextProvider,
    private logService: Logger,
    private router: Router,
    private route: ActivatedRoute,
    private dialog: MatDialog
  ) { }

  ngOnInit(): void {
    this.organizationId = this.getOrganizationIdFromUrl();
    this.initForms();
    this.states = this.referenceService.getAllOrganizationAddressStates();
    this.countryService.getCountries().then((countries: Country[]) => {
      this.countries = countries;
    }).finally(() => {
      if (this.loadingComplete()) {
        this.loading.next(false);
      }
    });
  }

  loadingComplete() {
    let countriesLoaded: boolean = this.countries !== undefined && this.countries !== null && this.countries.length > 0;
    return countriesLoaded;
  }

  initForms() {
    this.basicInformationForm = this.formBuilder.group({
      legalName: ['', [Validators.required, Validators.pattern(/[\S]/)]],
      displayName: ['', Validators.pattern(/[\S]/)],
      websiteUrl: ['', [Validators.required, Validators.pattern(/[\S]/)]],
      logoUrl: ['', Validators.pattern(/[\S]/)],
      addressForm: this.formBuilder.group({
        addressLine1: ['', [Validators.required, Validators.pattern(/[\S]/)]],
        addressLine2: ['', Validators.pattern(/[\S]/)],
        city: ['', [Validators.required, Validators.pattern(/[\S]/)]],
        state: ['', Validators.required],
        postalCode: ['', [Validators.required, Validators.pattern(/[\S]/)]],
        countryId: [1, Validators.required],
        countryName: ['']
      }),
      contactFirstName: ['', [Validators.required, Validators.pattern(/[\S]/)]],
      contactLastName: ['', [Validators.required, Validators.pattern(/[\S]/)]],
      contactEmail: ['', [Validators.required, Validators.email]],
      contactPhoneNumber: ['', [Validators.required, Validators.pattern(/^[\s()+-]*([0-9][\s()+-]*){10,11}(?:[\-\.\ \\\/]?(?:#|ext\.?|extension|x)[\-\.\ \\\/]?(\d+))?$/i)]],
      safelyServiceId: ['', Validators.required],
      sendCheckToSafely: [false],
      notifyGuestForAdditionalInfo: [false],
      notifyGuestOnVerificationComplete: [false],
      receiveEmailOnGuestVerificationComplete: [false],
      receiveVirtualCheckInAlert: [false],
      notificationEmail: ['', [Validators.required, Validators.email]],
      sendActivationLink: [true]
    });
  }

  populateNotificationEmail() {
    const adminEmailValue = this.basicInformationForm.get('adminEmail')?.value;
    this.basicInformationForm.get('notificationEmail')?.setValue(adminEmailValue); 
  }

  async submitOrganization() {
    this.loading.next(true);

    try {
      let organization = await this.buildOrganization();

      if (this.existingOrganization) {
        this.organizationId = (await this.organizationService.updateOrganization(organization)).id?.toString();
      } else {
        const organizationId = (await this.organizationService.createOrganization(organization)).organization_id;
        this.organizationId = organizationId?.toString();
        this.existingOrganization = true;
      }
      
      if (!this.organizationId) {
        this.logService.handleError('Error while submitting Organization - Organization ID does not exist', 'An error occurred while submitting Organization', 'Error while submitting Organization');
      } else {
        this.navigateToOrganizationDetailPage(this.organizationId);
      }
    } catch (e) {
      this.logService.handleError(e, 'An error occurred while submitting Organization', 'Error while submitting Organization');
    } finally {
      this.loading.next(false);
    }
  }

  async buildOrganization() {
    try {
      let organization: any = {
        ...this.basicInformationForm.value,
      }

      if (this.basicInformationForm.get('safelyServiceId')?.value === 0) {
        organization.for_reservation = 1;
        organization.for_guest_verification = 1;
      } else if (this.basicInformationForm.get('safelyServiceId')?.value === 1) {
        organization.for_reservation = 0;
        organization.for_guest_verification = 1;
      } else if (this.basicInformationForm.get('safelyServiceId')?.value === 2) {
        organization.for_reservation = 1;
        organization.for_guest_verification = 0;
      }

      organization.address = this.basicInformationForm.get('addressForm')?.value;
      organization.policy_billing_method_id = 1;
      organization.verification_billing_method_id = 1;
      organization.invoice_date = 5;
      organization.sync_all_properties = 1;
      organization.status_id = 1;
      organization.source_id = 1;
      organization.created_date = new Date();
      delete organization.addressForm;

      return organization;
    } catch (e) {
      this.logService.handleError(e, 'An error occurred while submitting Organization', 'Error while submitting Organization');
      return null;
    }
  }

  onNotifyGuestForAdditionalInfoChange(event: MatCheckboxChange) {
    if (!event.checked) {
      this.openNotifyUserForGuestInfoDeselect();
    }
  }

  openNotifyUserForGuestInfoDeselect() {
    this.dialog.open(NotifyAdditionalGuestInfoDialogComponent, {
      width: '55%', 
      height: '225px',
      disableClose: true
    });
  }

  getOrganizationIdFromUrl() {
    return this.route.snapshot.queryParamMap.get('id');
  }

  canCreateOrganization(): boolean {
    if (this.usCountrySelectedBoolean) {
      let validStateBoolean: boolean = false;
      if (this.basicInformationForm.get('addressForm.state')?.valid) {
        validStateBoolean = true;
      }

      if (validStateBoolean && this.basicInformationForm.valid) {
        return true;
      } else {
        return false;
      }
    } else {
      return (
        this.basicInformationForm.valid
      );
    }
  }

  navigateToOrganizationDetailPage(organizationId: string) {
    this.router.navigate([`/organizations/detail/${organizationId}`]);
  }

  onCountrySelectionChange(event: MatSelectChange) {
    if (event.value === 1) {
      this.basicInformationForm.get('addressForm.state')?.reset();
      this.basicInformationForm.get('addressForm.state')?.setValidators([Validators.required]);
      this.basicInformationForm.get('addressForm.state')?.updateValueAndValidity();
      this.usCountrySelectedBoolean = true;
    } else {
      this.basicInformationForm.get('addressForm.state')?.reset();
      this.basicInformationForm.get('addressForm.state')?.clearValidators();
      this.basicInformationForm.get('addressForm.state')?.updateValueAndValidity();
      this.usCountrySelectedBoolean = false;
    }
  }

  onPhoneNumberChanged(event: Event) {
    this.updatePhoneNumber((event.target as HTMLInputElement).value);
  }

  updatePhoneNumber(phoneNumber: string) {
    const formattedPhoneNumber = formatPhoneNumber(phoneNumber, this.userContextProvider.countryCode as CountryCode);
    this.basicInformationForm.patchValue({ contactPhoneNumber: formattedPhoneNumber });
  }
}